import { Link } from "@/components/i18n/LocaleLink";
import { ArrowRight, Heart, Map, Sparkles } from "lucide-react";
import { ROUTES } from "@/lib/routing";
import { useLocale } from "@/lib/i18n/LocaleContext";

function useQuickLinks() {
  const { t } = useLocale();
  return [
    { href: ROUTES.collection("dish"), label: t("nav.recipes") },
    { href: "/map", label: t("nav.map") },
    { href: "/tracker", label: t("nav.tracker") },
    { href: "/locations", label: t("nav.locations") },
    { href: "/guides", label: t("nav.guides") },
    { href: "/news", label: t("nav.news") },
    { href: "/achievements", label: t("nav.achievements") },
    { href: "/weather", label: t("nav.weather") },
    { href: "/events", label: t("nav.events") },
    { href: "/pets", label: t("nav.pets") },
    { href: "/daily-checklist", label: t("nav.dailyChecklist") },
    { href: "/codes", label: t("nav.codes") },
  ];
}

function useCollectionLinks() {
  const { t } = useLocale();
  return [
    { href: ROUTES.collection("fish"), label: t("nav.fish") },
    { href: ROUTES.collection("insect"), label: t("nav.insects") },
    { href: ROUTES.collection("bird"), label: t("nav.birds") },
    { href: ROUTES.collection("forageable"), label: t("nav.foraging") },
    { href: ROUTES.collection("crop"), label: t("nav.crops") },
    { href: ROUTES.collection("flower"), label: t("nav.flowers") },
    { href: ROUTES.collection("animal"), label: t("nav.animals") },
    { href: "/npcs", label: t("nav.npcs") },
    { href: "/shops", label: t("nav.shops") },
    { href: "/furniture", label: t("nav.furniture") },
    { href: "/wardrobe", label: t("nav.wardrobe") },
    { href: "/buildings", label: t("nav.buildings") },
  ];
}

export function Footer() {
  const { t } = useLocale();
  const quickLinks = useQuickLinks();
  const collectionLinks = useCollectionLinks();

  return (
    <footer className="mt-16 border-t border-border/70 bg-[linear-gradient(180deg,rgba(255,255,255,.65),rgba(250,246,240,.96))]">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-8 rounded-[2rem] border border-white/80 bg-white/70 p-6 shadow-[0_18px_50px_rgba(80,56,47,.08)] backdrop-blur md:grid-cols-[1.35fr_1fr_1fr] md:p-8">
          <div className="space-y-5">
            <Link to={ROUTES.home} className="group flex items-center gap-2.5">
              <div className="flex size-11 items-center justify-center overflow-hidden rounded-2xl bg-primary/15 transition-transform group-hover:scale-105">
                <img
                  src="/logo-64.png"
                  alt="Heartopia"
                  width={64}
                  height={64}
                  className="size-8 object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <span className="text-xl font-extrabold tracking-tight">
                Heartopia<span className="text-primary">.guide</span>
              </span>
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">{t("footer.description")}</p>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/map"
                className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2 text-xs font-extrabold uppercase tracking-[0.12em] text-background transition hover:opacity-90"
              >
                <Map className="size-3.5" />
                {t("nav.map")}
              </Link>
              <Link
                to="/tracker"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.12em] text-foreground transition hover:bg-secondary"
              >
                <Sparkles className="size-3.5" />
                {t("nav.tracker")}
              </Link>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-xs font-extrabold uppercase tracking-[0.18em] text-foreground">
              {t("footer.quickLinks")}
            </h3>
            <ul className="grid gap-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                    <ArrowRight className="size-3 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-xs font-extrabold uppercase tracking-[0.18em] text-foreground">
              {t("footer.collection")}
            </h3>
            <ul className="grid grid-cols-2 gap-2">
              {collectionLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center justify-between gap-3 px-2 text-xs text-muted-foreground sm:flex-row">
          <div className="flex items-center gap-2">
            <Heart className="size-3 fill-primary text-primary" />
            <span>2026 {t("footer.copyright")}</span>
            <span className="text-border">/</span>
            <span>{t("footer.disclaimer")}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/about" className="font-semibold hover:text-foreground">
              {t("nav.about")}
            </Link>
            <Link to="/support" className="font-semibold hover:text-foreground">
              {t("nav.support")}
            </Link>
            <Link to="/privacy" className="font-semibold hover:text-foreground">
              {t("legal.privacyTitle")}
            </Link>
            <Link to="/terms" className="font-semibold hover:text-foreground">
              {t("legal.termsTitle")}
            </Link>
            <Link to="/cookies" className="font-semibold hover:text-foreground">
              {t("legal.cookiesTitle")}
            </Link>
            <Link to="/sitemap" className="font-semibold hover:text-foreground">
              {t("legal.sitemapTitle")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
